/**
 * Dev-only: mirrors modules/pvpLadderSystem.js ladder rules for BattleOverview mock wins.
 * Keep in sync with server RANK_TIERS / computeWinOnLadder.
 */

/** @type {ReadonlyArray<{ key: string, maxStars: number }>} */
const RANK_TIERS = Object.freeze([
  { key: 'second_lieutenant', maxStars: 2 },
  { key: 'first_lieutenant', maxStars: 3 },
  { key: 'captain', maxStars: 4 },
  { key: 'major', maxStars: 5 },
  { key: 'lieutenant_colonel', maxStars: 5 },
  { key: 'colonel', maxStars: 5 },
  { key: 'brigadier_general', maxStars: 5 },
  { key: 'major_general', maxStars: 5 },
  { key: 'lieutenant_general', maxStars: 7 }
]);

const GENERAL_RANK_INDEX = 9;
const GENERAL_KEY = 'general';

function getMaxStarsForRankIndex(rankIndex) {
  if (rankIndex < 0 || rankIndex >= RANK_TIERS.length) {
    throw new Error(`devPvpLadderMock.getMaxStarsForRankIndex: invalid rankIndex ${rankIndex}`);
  }
  return RANK_TIERS[rankIndex].maxStars;
}

function getRankKey(rankIndex) {
  if (rankIndex >= 0 && rankIndex < RANK_TIERS.length) {
    return RANK_TIERS[rankIndex].key;
  }
  if (rankIndex === GENERAL_RANK_INDEX) {
    return GENERAL_KEY;
  }
  throw new Error(`devPvpLadderMock.getRankKey: invalid rankIndex ${rankIndex}`);
}

function computeWinOnLadder(rankIndex, stars) {
  if (rankIndex === GENERAL_RANK_INDEX) {
    throw new Error('devPvpLadderMock.computeWinOnLadder: already General');
  }
  if (rankIndex < 0 || rankIndex >= RANK_TIERS.length) {
    throw new Error(`devPvpLadderMock.computeWinOnLadder: invalid rankIndex ${rankIndex}`);
  }
  const maxStars = RANK_TIERS[rankIndex].maxStars;
  if (stars < 0 || stars >= maxStars) {
    throw new Error(
      `devPvpLadderMock.computeWinOnLadder: invalid stars ${stars} for rank ${rankIndex} (maxStars=${maxStars})`
    );
  }
  const nextStar = stars + 1;
  if (nextStar >= maxStars) {
    const newRank = rankIndex + 1;
    if (newRank === GENERAL_RANK_INDEX) {
      return {
        rankIndex: GENERAL_RANK_INDEX,
        stars: 0,
        promoted: true,
        reachedGeneral: true
      };
    }
    return {
      rankIndex: newRank,
      stars: 0,
      promoted: true,
      reachedGeneral: false
    };
  }
  return {
    rankIndex,
    stars: nextStar,
    promoted: false,
    reachedGeneral: false
  };
}

/**
 * One mock PvP win from a pre-General ladder position. Returns snapshot for AfterBattle and the new position for chaining.
 * @param {number} rankIndex
 * @param {number} stars
 * @returns {{ snapshot: object, nextPosition: { rankIndex: number, stars: number } }}
 */
function advanceDevMockPvpWin(rankIndex, stars) {
  const w = computeWinOnLadder(rankIndex, stars);
  const maxStars = w.reachedGeneral ? 0 : getMaxStarsForRankIndex(w.rankIndex);
  const snapshot = {
    rankIndex: w.reachedGeneral ? GENERAL_RANK_INDEX : w.rankIndex,
    stars: w.reachedGeneral ? 0 : w.stars,
    rankKey: w.reachedGeneral ? GENERAL_KEY : getRankKey(w.rankIndex),
    maxStars,
    promoted: w.promoted,
    reachedGeneral: w.reachedGeneral,
    starsDelta: 1,
    mmrBefore: 1000,
    mmrAfter: w.reachedGeneral ? 1000 : 1000,
    mmrDelta: 0,
    inLeague: w.reachedGeneral
  };
  const nextPosition = {
    rankIndex: w.reachedGeneral ? GENERAL_RANK_INDEX : w.rankIndex,
    stars: w.reachedGeneral ? 0 : w.stars
  };
  return { snapshot, nextPosition };
}

export {
  RANK_TIERS,
  GENERAL_RANK_INDEX,
  GENERAL_KEY,
  getMaxStarsForRankIndex,
  getRankKey,
  computeWinOnLadder,
  advanceDevMockPvpWin
};
